import React, { useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Tabs,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomTab from "components/CustomTab";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import CardDashboardItemVerticalMulti from "components/CardDashboardItemVerticalMulti";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";

const Help = () => {
  usePageTitle("Help");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  // Tabs: 0 = Changelog, 1 = Videos. True tabs on every screen size — only
  // the active panel renders, so Videos is reachable from its tab and never
  // by scrolling past the end of the Changelog (Amine, 2026-10-05).
  const [tabValue, setTabValue] = useState(0);

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Array of tutorial videos organized by category
  const tutorialVideos = [
    {
      category: "Getting Started",
      videos: [
        {
          name: "Introduction to CE Portal & Dashboard",
          description:
            "Learn the basics of navigating the CE Portal interface, as well as the Dashboards for each service.",
          link: "https://youtu.be/V2P398FY05Y",
          thumbnailUrl: "https://img.youtube.com/vi/example1/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Interpretations",
      videos: [
        {
          name: "Add Interpretation Assignment",
          description:
            "How to create and set up a new interpretation assignment",
          link: "https://youtu.be/UbDJMS89flE",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "Edit Interpretation Assignment",
          description: "How to edit a interpretation assignment",
          link: "https://youtu.be/Eccb1hF3064",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "All Interpretations",
          description: "Browsing through all the interpretation assignments",
          link: "https://youtu.be/eVn1LMym0dw",
          thumbnailUrl: "https://img.youtube.com/vi/example6/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Translations",
      videos: [
        {
          name: "Add Translation Assignment",
          description: "How to create and set up a new translation assignment",
          link: "https://youtu.be/fVgh_W0OxYU",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "Edit Translation Assignment",
          description: "How to edit a translation assignment",
          link: "https://youtu.be/xOJMkINcRr8",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "All Translations",
          description: "Browsing through all the translation assignments",
          link: "https://youtu.be/Sr3v8D6kJp0",
          thumbnailUrl: "https://img.youtube.com/vi/example6/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Other",
      videos: [
        {
          name: "All about the Other Assignments",
          description: "Create, Edit, and Browse through the Other Assignments",
          link: "https://youtu.be/f7McCAA1uPQ",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Connections",
      videos: [
        {
          name: "Linguists",
          description: "Add, Edit, and Check Linguists Details",
          link: "https://youtu.be/I8l9s4Aq7W0",
          thumbnailUrl: "https://img.youtube.com/vi/example7/mqdefault.jpg",
        },
        {
          name: "Clients",
          description: "Add, Edit, and Check Clients Details",
          link: "https://youtu.be/CSlfZ0RKJnc",
          thumbnailUrl: "https://img.youtube.com/vi/example8/mqdefault.jpg",
        },
      ],
    },
  ];

  /**
    {
      version: "0.9.25",
      date: "05-14-2026",
      changes: [
        "Added 'Email Templates' feature: Allows users to create and manage email templates for various notifications.",
        "Added 'Invite Linguist to Portal' feature:",
        "- Members can invite Linguists to the Portal.",
        "- Invited Linguists will receive an email to set up their password and access the Portal.",
        "- Once logged in, Linguists can receive emails of new offers for assignments.",
        "- Members can invite (offer) one or multiple linguists to the assignment.",
        "- If a linguist doesn't accept the offer, Members can send reminders via Email or Text (From Portal).",
        "- Members get notified when a linguist accepts the offer.",
        "- ",
        "- ",
        "- ",
        "- ",
        "Added 'Linguist Portal' feature:",
        "- Allows linguists to access their assignments and manage their profiles through a dedicated portal.",
        "- Allows Linguists to set up their profile and update their data and notification preferences.",
        "- Allows Linguists to finalize assignments after those assignments are done.",
        "- Notifies Linguists when a new assignment is offered to them, or when another linguist accepts it before them.",
        "- Allow Linguists to see details of assignments that they were assigned to.",
        "- ",
        "- ",
        "- ",
        "- ",
      ],
    },  */
  // Array to store version history and changes
  const versions = [
    {
      version: "0.9.63",
      date: "10-19-2026",
      changes: [
        "Invoices list: the Paid, Unpaid, and Past Due totals under the grid now always match the rows on screen. Previously they always covered the whole date range, even when a filter was applied, so the totals could show hundreds of invoices above a grid that was showing only a few.",
        "Invoices list: Paid, Unpaid, and Past Due are now three separate totals — every invoice is counted in exactly one of them, and the three counts add up to the number of rows shown. Past Due means the invoice is unpaid and its due date has already passed; Unpaid means it is not paid yet and is not past due.",
        "Invoices list: an old saved filter could hide rows without any visible filter being active. Saved filters are now cleaned up automatically, so the grid always reflects the filters you can see in the toolbar and clear with Clear Filters.",
        "Invoices list: the Late By column now handles a blank payment date correctly — invoices with no payment date recorded show their days late instead of N/A.",
        "New Invoice: a Payment Link is now required only when Credit Card is the client's ONLY payment method. If the client also accepts another method (check, ACH, and so on), the link is optional and the invoice can be saved without one.",
        "Invoice Profile — Export PDF: the same rule now applies to printing and exporting — the Payment Link is only required for clients whose only payment method is Credit Card.",
        "Quick search (the search box in the top bar): invoice results now show the client's name and whether the invoice is Paid or Not Paid — in both the dropdown and the full Search Results window.",
        "Reports — Outstanding Invoices: payment status is now read the same way as everywhere else in the Portal, so an invoice with no payment date recorded is always treated as unpaid.",
      ],
    },
    {
      version: "0.9.62",
      date: "10-13-2026",
      changes: [
        "Validation Checks: checks can now be organized for All Services, Interpretations, Translations, or Other Services. Service-specific checks, including mileage and contact-person checks, are grouped under Interpretations, and the scope migration now reapplies the intended categories.",
        "Interpretations, Translations, and Other Services: added consistent Checks columns and filters for Passed, Warnings, and Errors. The same teal, amber, and red square indicators now appear in lists, profiles, tooltips, and side panels, with matching icons and 8px status-chip corners.",
        "Validation Checks settings: fixed condition fields that were not filling when editing checks, clarified which formula-based checks can be customized, and moved severity editing into the edit window.",
        "Service side panels: now show the assignment details from the saved columns across Interpretations, Translations, and Other Services. Address details include copy and map actions, and the three-dot menu now offers the same relevant actions as the assignment profile.",
        "Interpretation distance calculations: moved ORS requests to the current api.heigit.org endpoints. Failed requests now explain that the Distance Calculation Server is unavailable and offer Google Maps directions for manual distance checks.",
        "Distance cache: only valid positive distances are saved or reused. Added a migration to remove existing null, zero, and negative cache entries so failed calculations cannot become cached values.",
        "Overlays: applied the text-field border color rule consistently, including the Linguist and Client New/Edit overlays.",
      ],
    },
    {
      version: "0.9.61",
      date: "10-12-2026",
      changes: [
        "Automatic checks: a check can now apply to all services, any two services, or a single service — checks that make sense everywhere (like a completed assignment with no charge) now cover Interpretations, Translations, and Other Services at once.",
        "Automatic checks: the built-in checks now also run in the Translations and Other Services side panels — the same green, orange, and red indicators the Interpretations list already shows.",
        "Settings → Validation Checks: built-in checks are now fully editable — open one to see exactly how it's constructed and adjust its conditions and metadata. Formula-based checks show a note explaining that their core calculation is part of the Portal; their 'Applies When' conditions still work as an extra gate.",
        "Settings → Validation Checks: when creating or editing a check, pick one, two, or all three services with the new checkboxes — the condition columns offered adapt to the services you select.",
        "Settings → Validation Checks: checks that cover all three services are now collected in their own 'All Services' group at the top, and every other check is listed under each service it applies to — a 'Translation + Other Service' style badge marks the checks that cover more than one service.",
      ],
    },
    {
      version: "0.9.60",
      date: "10-09-2026",
      changes: [
        "Interpretations list: new 'Checks' column, right after the Number — a green check when the assignment looks right, and a red or orange indicator when something needs attention — hover the indicator to see exactly what's wrong, issues listed first.",
        "Interpretations list (desktop): clicking an assignment now opens a compact panel beside the list with its details and its check results — schedule, client data, costs, contact persons, and email threads included — so you can review without leaving the list. Drag the panel's left edge to resize it (it remembers your width), click another row to swap its content, or the X to close it.",
        "Automatic checks: every assignment is checked on its own — an in-person assignment with no mileage recorded, a virtual or phone assignment that carries mileage, a cancelled assignment that still shows charges, a Simultaneous rate identical to the linguist's standard rate, actual times out of order, preparation and scheduling longer than the total duration, a completed assignment with no actual times, or a Confirmed/Completed assignment with no linguist. Red items mean the numbers don't add up; orange items are worth double-checking.",
        "Automatic checks: the same indicators appear on the Interpretation profile, and the exported list includes a Checks column that reads 'OK' or the number of issues for each assignment.",
        "Settings → Validation Checks (new tab): every check is listed, and you can turn any of them on or off right in the list — a check with a tolerance setting (like the Simultaneous rate comparison) can have that adjusted inline too. The add button now matches the '+' button style used across the other Settings tabs.",
        "Settings → Validation Checks: the New / Edit window lets you build your own checks from conditions — choose a field, an operator (equals, does not equal, is blank, is one of, greater than…), and a value, and combine several conditions with AND/OR.",
        "Settings → Validation Checks: the built-in checks can be adjusted but not deleted — disable them instead; your own checks can be edited and deleted anytime. Checks for Translations and Other Services can already be created in Settings — their results will appear once those lists get the same Checks column.",
        "Total Linguist Pay: the base Linguist Pay and Mileage always count toward it — assignments that previously showed N/A or dropped the mileage now show the correct figures.",
        "Manual-mode assignments (Disable Automatic Charge Calculation): Total Linguist Pay and Total Cost now carry over from the Linguist Pay row plus the included cost lines — previously they were recalculated from the rate × duration formula. A typed Total Linguist Pay that doesn't reconcile with Linguist Pay + included lines now shows a warning in the editor, with the exact figures.",
        "Lists, profiles, the side panel, and exports: the figures shown are now exactly the saved ones — the Portal no longer shows a different number in one place than another.",
        "Cancelled assignments: 'Cancelled (Without 24 Hrs Notice)' assignments are no longer flagged when they carry charges — only 'With 24 Hrs Notice' and 'Unable To Fill' cancellations are expected to show $0.",
        "Settings popups: the back and maximize buttons in popups opened from Settings (like the New/Edit Validation Check window) no longer show a background box — they now match the buttons in every other popup.",
      ],
    },
    {
      version: "0.9.59",
      date: "10-08-2026",
      changes: [
        "Invoice PDF export: exported invoice PDFs are now real text documents — you can select, copy, and search everything in them, and they are much smaller than before, so they open instantly instead of taking a while on some devices. The design of the invoice is exactly the same.",
        "Invoice PDF export: multi-page invoices now export correctly with each page on its own PDF page — previously a multi-page invoice could be squeezed onto a single page.",
        "Invoice PDF export: the phone number, email, and website in the invoice footer are clickable in the exported PDF — including the phone number on the last page, which was not clickable before.",
        "Invoice Profile — Export PDF: a normal click exports with the new format. If you ever need the previous image-based PDF, press and hold 'Export PDF' for 3 seconds — the button shows \"Keep holding for legacy export…\" while you hold.",
        "OPI Upload — Download Detailed Logs ZIP: the detailed logs PDFs are now styled exactly like the invoice — the same letterhead with the CE logo, the same footer with the contact details, and a new Period line under the title showing the covered date range (for example \"August 1 – August 31, 2026\"). The tables are now landscape so the columns fit comfortably, and longer logs continue onto additional pages with page numbers.",
        "OPI Upload — Download Detailed Logs ZIP: the download now shows live progress (per client and per page) and reports any problem clearly instead of closing silently — and it never produces an empty ZIP when there are no rows to export.",
        "Invoice Profile — Export PDF: the progress window now says \"Preparing legacy PDF…\" when the previous image-based export is used, so it is clear which format is being generated.",
      ],
    },
    {
      version: "0.9.58",
      date: "10-07-2026",
      changes: [
        "Interpretation and Translation New/Edit: Email Thread Links can now hold MULTIPLE links per assignment — the section now matches the Costs style (title with a + button and a divider), each row has its own paste button and remove button, and every link is checked as you enter it: a valid Outlook link shows a green check, while an unrecognized link gets a red border and blocks saving until it's fixed. Translations now support email thread links too, and only the thread ID is stored behind the scenes.",
        "Assignment profiles (Interpretations and Translations): each saved email thread now has its own 'Open Email Thread' button — numbered when the assignment has several links.",
        "Calendar: right-clicking an assignment now offers 'Open Email Thread' for every saved link, a matching 'Copy Email Thread Link' action, and a new 'Copy Project Number' action.",
        "History: email thread link changes now display as a readable list instead of raw data.",
        "Settings > Linguist Documents and Settings > Service Documents: the document cards were redesigned — a document icon badge, the Active/Archived status as a colored chip, a clean info grid (Size / Version / Updated, or File / Fields), and the actions are now labeled buttons (View / Download / Edit / Delete) that always fit inside the card instead of overflowing.",
        "Invoice Edit: changes to a project's Details now appear on the invoice profile immediately after saving — previously the profile kept showing the old details until a manual reload. Clearing a project's Details now also truly clears them.",
        "Invoice Edit: fixed the cursor jumping to the end of the text while editing a project's Details inline — you can now place the cursor anywhere in the text and type normally.",
      ],
    },
    {
      version: "0.9.57",
      date: "10-06-2026",
      changes: [
        "Linguists: every language now has its own 'Can handle Simultaneous' option with a Simultaneous Rate — by default the Simultaneous Rate is the SAME as the linguist's regular rate, and it only differs if you set your own. Unchecking the option marks the linguist as unavailable for Simultaneous assignments in that language (the Simultaneous Rate field is disabled), and the language rows are now a clean table: Language, Rate, Simultaneous, and Simult. Rate.",
        "Interpretation New/Edit: selecting a linguist now loads the rate for the assignment's LANGUAGE — previously it could pick a different language's rate. Simultaneous assignments load the linguist's Simultaneous rate (falling back to their regular rate when none is set). A linguist who doesn't handle Simultaneous can still be assigned — the picker marks them '(No Simultaneous)' and the warning reads \"This linguist doesn't handle Simultaneous assignments — enter the rate manually.\"",
        "Interpretation New: a Simultaneous assignment can now be created without a linguist — the simultaneous-rate check used to block the save unless a placeholder 0 was entered. Rate fields are only required once a linguist is selected, so an assignment can go straight into Pre-Assign Outreach.",
        "Simultaneous assignments: scheduling, preparation, and travel time are always billed at the consecutive rate — only the actual assignment duration uses the Simultaneous rate.",
        "Pre-Assign Outreach: on Simultaneous assignments, linguists who don't handle Simultaneous are marked with a warning icon, so you can see at a glance whose rate will need to be entered manually.",
        "Linguists list and profile: when a linguist has a different Simultaneous rate, the Rate column shows both compactly (e.g. $30 / $40) — hover it (or tap the '?' on mobile) to see which rate is which, and the linguist profile groups the rates by language. Previously the list tried to spell both rates out and took too much space.",
        "History: when both rates of a language change at once, History now lists each mode's rate change separately — Consecutive and Simultaneous — instead of one combined entry, so you can see exactly what changed for each mode.",
        "Interpretation New/Edit: added an 'Email Thread Link' field — paste the whole Outlook email thread link instead of picking out the ID yourself, so there is no risk of copying the wrong part of the link; the Portal saves only the thread ID behind the scenes. The button on the field's edge pastes the link straight from your clipboard, and you can always type or paste into the field manually.",
        "Interpretation Profile: the Assignment card now shows an 'Email Thread' row with an 'Open Email Thread' button that opens the saved thread in Outlook — the link is rebuilt from the stored ID. It shows N/A when no link was saved.",
        "Interpretation History: changes to the email thread link now appear as 'Email Thread Link'.",
      ],
    },
    {
      version: "0.9.56",
      date: "10-01-2026",
      changes: [
        "Sidebar: marking an assignment as paid (or unpaid) now updates the Invoices count on the sidebar immediately — previously the badge stayed stale until an unrelated change happened.",
        "Interpretations list: right-click an assignment → Pre-Assign Outreach — contact and track linguists for a single assignment straight from the list (same screen as the assignment profile).",
        "Pre-Assign Outreach: the Text (SMS) button now opens your texting app with the availability message pre-filled — assignment, date, time with duration, and the location reduced to City, State ZIP (the street is never shared when checking availability), signed with your name.",
        "Pre-Assign Outreach: new status 'Waiting for Answer' — mark a linguist you reached out to and are expecting a reply from.",
        "Translations and Other Services editors: the duplicate Rate field was removed — Unit Rate is the rate now (older assignments still fall back to their saved Rate). Translation/Service Status moved into Rate's spot, and Unit Type / Unit Count / Unit Rate now span the full row.",
        "Notifications: split into tabs — All, Interpretations, Translations, Reminders, and Tags — each showing its unread count; notification type chips now show clean labels (e.g. 'Interpretation Starting', 'Mention') instead of raw internal names.",
        "Translations and Other Services editors: the calculation breakdowns under Account Charge, Total Cost, and Total Linguist Pay now explain themselves — hover any number (unit count, number of languages, rate per unit, or a named extra like Mileage) to see what it is, same as the Interpretation editor. Numbers with an explanation show a dotted underline.",
        "Mileage: the automatic 'Double check the mileage rate' task was too strict — an assignment priced correctly to the cent could still be flagged over fractions of a cent of rounding. The check now compares totals (2¢ tolerance), so it only appears for genuine differences. Assignments dated in a year that has no mileage rate set yet now consistently use the most recent rate in effect.",
        "Pre-Assign Outreach SMS: the location line now shows the full City, State ZIP — some addresses showed only the ZIP (e.g. 'PA 17603') when the address was typed across multiple lines. The street is still never shared when checking availability.",
        "Translations and Other Services editors: Unit Rate no longer shows empty on older assignments — when an assignment has no stored Unit Rate, the field now falls back to its saved Rate (the rate it was created with), and it opens in the normal style (the automatic fallback no longer shows the blue 'unsaved change' border).",
        "Notifications: the bell menu now has the same group filter as the Notifications page — All, Interpretations, Translations, Reminders, and Tags — as a dropdown that follows the standard field style and always starts on All each time the menu is opened.",
      ],
    },
    {
      version: "0.9.55",
      date: "09-23-2026",
      changes: [
        "Reminders: copying a reminder message now also marks that linguist's assignments as reminded (previously only sending the SMS did) — the Reminded / Partially reminded indicator reflects it either way.",
        "Notifications: team-only alerts (Reminder Watch, service deadline reminders, task notifications) can no longer appear in the Linguist Portal — they could previously show up in a linguist's notifications when the linguist's account number happened to match a team member's number. Reminder Watch itself is unchanged for the team: every active member is still alerted.",
        "Edit Interpretation Assignment: the calculation breakdown under Account Charge, Total Linguist Pay, and Total Cost now explains itself — hover any number in the formula to see what it is (rate, duration, preparation/scheduling time, mileage, parking, travel time, …). Numbers with an explanation show a dotted underline.",
        "Edit Interpretation Assignment: removing a cost from the list now truly removes it — it stops counting toward Account Charge, Total Linguist Pay, and Total Cost immediately and stays out after saving. Travel Time was the worst case: its amount kept coming back from the saved duration and could even re-create the row you deleted when the client rate changed. Zeroing a value before removing its row is no longer needed.",
        "Mileage: removing the Mileage row now removes the mileage for good — previously opening the assignment re-added the mileage cost by itself (computed from the saved roundtrip miles), so it never actually left the database. Removing the row also clears the roundtrip miles, matching how the portal treats assignments without a mileage cost. No need to zero anything first anymore, for Mileage either.",
        "Manual-mode assignments (Disable Automatic Charge Calculation): Total Linguist Pay now saves and stays — the figure is stored on the assignment and shown back exactly as saved. It is no longer recalculated from the cost list when the assignment is opened, and saving no longer replaces it. Existing manual assignments have been corrected so the stored figures match what the portal has been displaying.",
        "Notes on assignments (Interpretations, Translations, Other Services): each note now has the same 3-dots (⋮) menu as the Notes on Client and Linguist profiles — Edit and Delete are visible on every note, on both the desktop and mobile layouts. Previously a note could only be edited by right-clicking it, which made notes look uneditable (and was impossible on touch screens).",
        "History (assignments, clients, linguists): changes made by the system itself — scripts, data repairs, background jobs — now show as \"Server\" instead of \"N/A\" (for example, \"Server updated the assignment\"). Team members' own actions are unchanged.",
        "Linguist profile (edit overlay): the tooltips for the primary-phone star, Save, and Discard buttons now follow the theme — light card style in light mode, dark card style in dark mode — instead of the fixed grey bubble. In dark mode the default tooltip was white-on-white (unreadable); that is fixed everywhere tooltips appear, not just on this screen.",
        "Calendar: the \"Verify Outlook Sync\" button (admins) now matches the \"Add Interpretation\" button style — solid primary fill, same size, radius, and shadow.",
        "Overlays (every popup in the portal — editors, profiles, merge screens, lists): they all now share ONE template, so every overlay looks and behaves the same — same header with the back arrow and title, same bottom bar with the action buttons (Delete on the left, Discard and Save on the right — identical across every overlay), same white content card. Over 50 overlays were converted, including every Settings editor (languages, dialects, units, holidays, mileage rates, payment methods, service types, cost types, documents, templates, notifications, requirements, expense categories/subcategories…), client/linguist/requestor/task/expense editors and all their notes, the interpretation/translation/other-service editors, the merge screens, New Invoice, the manual timesheet editor, the generated-documents list, Pre-Assign / Outreach, the profile editor, and the document template editor.",
        "Overlays can now be moved and maximized everywhere: drag an overlay by its header to get it out of your way (it can't be dragged off-screen), and use the fullscreen button in the header to maximize and restore it. The portal remembers where you left each overlay — and whether it was maximized — so reopening it puts it back exactly where you had it. Fixed: overlays were jumping back to the center when you saved an edit.",
        "Text fields: empty fields now show a visible grey border everywhere — on several screens the border was white-on-white, so fields looked like they had no outline at all. Filled fields show the green border, fields with unsaved changes the blue one, and required/missing fields the red one — same rule on every overlay.",
        "Dropdown lists inside overlays (client pickers, language pickers, requestor/invoice pickers…) are no longer cut off by the overlay's content area — the list now opens fully above the overlay. This covered Pre-Assign, the assignment editor, the translation and other-service editors, Rates, and Translation Documents.",
        "Wide overlays now use their full intended width: Pre-Assign Outreach, the merge screens (Linguists / Clients / Requestors / Contact Persons), New Invoice, and the Document Template editor were being held narrower than designed, squeezing their wide tables and side-by-side lists — they now have the room they were built for.",
        "Pre-Assign Outreach (both the search screen and the outreach list on the assignment profile) and the Linguist Payments profile: the overlay itself no longer scrolls — the summary stays fixed and only the list scrolls, with its header row staying visible. The payments table now has its own fixed-height scroll instead of stretching the overlay.",
        "Small editors now size to their content instead of stretching to a fixed height — Settings editors, notes, expenses, tasks, and the timeslot editor no longer open with a big empty area under the fields.",
        "New Invoice: the Projects / Invoices tabs now use the standard overlay tabs (same style as the rest of the portal), each tab shows how many rows it holds — Projects (12), Invoices (3) — and the invoice table now reaches the bottom of the overlay, sitting flush under the tabs.",
      ],
    },
    {
      version: "0.9.54",
      date: "09-22-2026",
      changes: [
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.9.53",
      date: "09-21-2026",
      changes: [
        "Lists now keep your scroll position on every automatic refresh — when a live update from another member, a save, or a filter reloads a list, your place is kept instead of jumping back to the top. This now covers every list in the app: Linguists, Clients (Requestors, Groups, Contact Persons, History), the linguist's service history, Services, Invoices, Linguist Payments, Expenses, Members, Bug Reports, and the assignment tables on the All Linguists dashboard.",
        "Dashboards (monthly and weekly Interpretations, Translations, and Other Services views): the assignment tables keep their scroll position when the dashboard refreshes.",
        "Lists (Interpretations, Translations, Other Services): live updates from other members are now filter-aware — a change only reloads your list when it affects an assignment visible in your current filter — and your scroll position is preserved after saving an edit (no more jumping back to the top).",
        "Pre-Assign Outreach (on the Assignment Profile): outreach changes now update live for everyone viewing it — when a teammate changes a status or assigns a linguist, other users' screens refresh automatically.",
        "Calendar (new): 'Verify Outlook Sync' button (admins only) — pick one or more months and check every assignment against the Outlook calendar. The check reports which assignments are missing, deleted from Outlook, or out of date, then sync them your way: one at a time from the button on each card, long-press a card to select several and sync the selection, or sync all at once.",
        "Pre-Assign Outreach: assigning a linguist now applies their rate (and the 2-hour minimum when applicable) and recalculates the assignment's Linguist Pay, Travel Time, and totals — previously only the linguist was set and the costs stayed unchanged.",
        "Simultaneous Rate now defaults to the SAME rate as the regular rate (the automatic 1.5× markup was removed) — when a linguist needs special simultaneous pricing, enter it manually on the assignment.",
        "Send Details: your selected email template now sticks — the overlay no longer blinks and jumps back to the default template when you copy the email package (a manually picked template is preserved).",
        "Contact Persons: adding a NEW contact person with a phone number on an assignment now saves the number correctly (previously it was silently dropped), and contact phone numbers now display reliably when you reopen the assignment.",
        "Logs Viewer: entries now show which user they belong to (when applicable) and can be filtered by user — batched entries from several users list them all; real-time (WebSocket) activity and background jobs (like the notification cleanup) now log in the same standard format as everything else. The viewer itself got a cleaner layout: grouped filter panel, entry count summary, and color-coded entries.",
        "Calendar & Outlook sync: sync activity is now fully captured in the Logs Viewer — a start line per sync, the reason for every assignment that fails (with the underlying Outlook error), and a done summary. Server restarts also appear in the logs, and chat (WebSocket) disconnections log their reason, so sync issues can be traced from the portal without server access.",
        "Calendar sync: large batches now show live progress — clicking Sync opens a progress panel that updates as it goes: how many are done, how many are left, the assignments currently syncing, and running totals (synced / failed / skipped), with a Stop button. It also no longer times out on big batches: the sync is sent in small batches instead of one long request, so a full month of 100+ assignments completes reliably.",
        "Logging overhaul: every server error now lands in the Logs Viewer — all remaining modules were converted to the standard logging pipeline, so route failures, background job failures, and rejected requests (expired or denied sessions) are recorded instead of being silently dropped.",
        "Logs Viewer — related lines: when you filter by a level (for example Errors), the lines that belong to the same request as a shown entry — its OK/WARN trail, even async follow-ups — now appear right next to it, marked 'Related'. The 'Show related lines' switch turns this on or off, so you can read an error together with the context that led to it without clearing your filter.",
        "Protect PDF: each file now shows its own result next to its name (protected ✓ or the failure reason) instead of a long summary message.",
        "Merge (Linguists, Clients, Requestors, Contact Persons): the confirmation dialog now shows a preview of what will move to the kept record and which duplicates will be skipped.",
        "Linguists, Requestors, and Contact Persons: added a star button to choose which phone number is Primary.",
        "Password fields: a consistent strength meter (minimum 8 characters) with clear requirement checkmarks.",
        "Service Documents: generated document lists now have 'Load more' pagination, batch downloads show progress, duplicate file names are rejected with a clear message, and the document editor shows when someone else is editing.",
        "Notifications: notifications older than 60 days are now cleaned up automatically by the server.",
        "Reminders and Send Details: added the full formatting toolbar (bold, italic, underline, lists, indent, hyperlink).",
        "Reminder Watch: alerts now reach EVERY team member — previously only the person who set the watch rule was notified, so the rest of the team never saw the 'Reminders not sent' warning. If several rules are set, everyone gets one alert (not one per rule).",
        "Project numbers are now assigned by the server when an assignment is created — a new assignment can no longer receive a number that is already in use (previously, adding assignments for the same day from two screens — or with an outdated count — could produce a duplicate number). A number you type manually is kept as long as it is free; otherwise the next free number for that day is assigned automatically.",
        "Multiple bug fixes and improvements.",
      ],
    },
    {
      version: "0.9.52",
      date: "09-18-2026",
      changes: [
        "Reminders: fixed the date filter — opening the overlay now shows exactly the selected day's assignments (the Friday→weekend expansion only applies to the default view, and only when today is Friday).",
        "Reminders: the date field is now a proper portal-styled MUI date picker.",
        "Reminders: sending a reminder now records it on the assignment (ReminderSentOn/ReminderSentBy) and the row shows a 'Reminded' chip; partially reminded linguists show 'Partially reminded'.",
        "Reminders: for linguists without a phone number, copying the message now counts as sending the reminder.",
        "Reminder Watch (new): a Settings tab where you create rules that check tomorrow's assignments and notify you when reminders were not sent — with recurrence until sent, partial-send awareness, and click-through to the Reminders overlay. Check times are labeled America/New_York (EST).",
        "Service Documents: Protect PDF now accepts multiple PDFs at once — same password, each file downloads individually, with per-file error reporting.",
        "Bug Reports: new admin-only 'View Logs' screen — pick a log file and time period, see info/warn/error/crash counts, filter by level or search, and page through entries.",
      ],
    },
    {
      version: "0.9.51",
      date: "09-16-2026",
      changes: [
        "Interpretation New/Edit: in-house linguists (rate $0, paid monthly) can now save Simultaneous assignments — a $0 simultaneous rate is no longer treated as a missing value and no longer blocks saving.",
        "Service Documents: the document picker now shows every category, language, and variant at once (no more drill-down), and variants are ordered Standard first, then Notarized.",
        "Service Documents: added a Document Categories manager in Settings (create/edit/delete categories used by the document picker).",
        "Services lists: the Status submenu in the right-click context menu now behaves like a Windows context menu — it closes instantly when you hover another item.",
        "Fixed a crash in the Activity/History feed when an audit log entry contained a null value.",
      ],
    },
    {
      version: "0.9.50",
      date: "09-15-2026",
      changes: [
        "Notifications: interpretation notifications now fire only for Confirmed assignments (Requested and Confirmed (Follow-up) no longer trigger them).",
        "Outlook Calendar sync is now more robust: failures are logged to the server log, the sync retries once on transient errors, and non-production environments no longer pretend the sync succeeded.",
        "Fixed an issue that created duplicate 'N/A' linguists — saving a service without a real linguist now reuses the existing N/A linguist.",
        "Calendar: future dates now load correctly (fixed a year-boundary cache issue and arrow navigation that jumped back to today).",
        "Settings: the presence indicator now shows which tab each member is viewing (e.g. 'Settings > Timesheets').",
        "Outreach/Pre-Assign: the mileage cache now matches addresses more reliably (punctuation variants hit the same cached distance) and reports its source honestly (CACHE / PARTIAL / API).",
        "Interpretation New/Edit: adding a phone number to an existing Contact Person now saves correctly (no more duplicate contacts).",
        "Timesheets: multi-line notes now show line breaks.",
        "Invoices: 2-Hr-Min assignments now show 2 hours in the invoice (Profile and Edit).",
        "Invoice Edit: the Unit column now shows the new Unit system (e.g. '330 Pages') instead of N/A.",
        "Invoice New: the Service Category column now shows the specific subtype (e.g. 'Transliteration') instead of the generic category.",
        "Timesheets: the 'Median So Far' card is now 'Average' and computes the average per entry.",
        "Service audit log: toggling the Linguist Pay (or Total Cost / Account Charge) inclusion flag on a cost line now appears in the History.",
        "History tab: all entries now load (no more 50-entry clip) on Service, Client, and Linguist profiles.",
        "Invoice Profile: the 'Other Invoices' section now shows a Paid / Not Paid / Overdue badge on each invoice.",
        "Invoices: Invoice Options and Print Options are now saved as metadata on the invoice and restored when it is reopened.",
        "Timesheets: durations now use the compact format (1h22) and the total includes the current in-progress time (1h22 (~total)).",
        "History: zero-value and formatting-only cost changes (N/A → $0.00, 330.00 → 330) no longer create noise entries.",
        "Invoices: Transliteration services are now split by language correctly (one line per target language, source separated).",
      ],
    },
    {
      version: "0.9.46",
      date: "09-08-2026",
      changes: [
        "Service Costs: added a new cost-inclusion model — every cost line now has three checkboxes (Total Cost, Total Linguist Pay, and Account Charge) that control exactly where the cost is added, with any combination allowed (at least one must be checked).",
        "Service Costs: the default inclusion for each cost type is now managed from Settings > Cost Types, and each assignment can override the defaults per cost line.",
        "Service Costs: Payment Fees are now always excluded from the Account Charge (they only count toward Total Cost and Total Linguist Pay).",
        "Service Costs: Total Linguist Pay is now calculated from the cost lines' inclusion flags instead of a fixed list of cost types.",
        "Fixed an issue that caused updating a Linguist's rate to corrupt the costs of their future assignments (missing mileage, parking, and other costs) — future assignment costs are now recalculated from the cost lines, and invoiced or paid assignments are skipped.",
        "Fixed an issue that caused the automatic cost rollup to overwrite a manually set Account Charge.",
        "Fixed the Merge functionality:",
        "- Merging duplicate Clients now works correctly (previously failed with an error).",
        "- Merging duplicate Linguists now preserves their dialects, outreach history, notification preferences, invitations, offers, and login history (previously these were lost or blocked the merge).",
        "- Merging duplicate Requestors now correctly updates their Requestor Groups (Primary Invoicer, CC recipients, and group membership).",
        "- All merge operations now show a confirmation dialog before merging, since the action cannot be undone.",
        "Multiple bug fixes and improvements.",
      ],
    },
    {
      version: "0.9.45",
      date: "08-31-2026",
      changes: [
        "Invoice Profile: the 'Split Languages' checkbox now also splits Other Services with multiple target languages (e.g. Voiceover, Video Synchronization) into one line per language, with the charge divided evenly — same behavior as Translations.",
        "Interpretation Service Costs: Travel Time is now billed at half the rate with two separate values — the Account Charge uses the consecutive Client Rate, while Total Linguist Pay and Total Cost use the consecutive Linguist Rate (even for Simultaneous assignments).",
        "Interpretation Service Costs: the Travel Time cost row now shows 4 fields side by side — Cost Type, Travel Time Duration, Travel Time Cost (Client), and Travel Time Cost (Linguist) — with adaptive border colors matching the other cost fields.",
      ],
    },
    {
      version: "0.9.44",
      date: "08-26-2026",
      changes: [
        "Interpretation Reminders: on Fridays, the reminders now include Saturday, Sunday, and Monday assignments (the three days after the selected Friday) — if there are no weekend assignments, the reminders behave exactly as before. All other weekdays are unchanged.",
        "Fixed the Send Reminders SMS link to use the linguist's correct phone number from the dedicated phone-number table (primary number first), with a clean dialable format for the sms: link.",
      ],
    },
    {
      version: "0.9.43",
      date: "08-24-2026",
      changes: [
        "Fixed the Invoice OneDrive link to open the correct folder: unpaid invoices and invoices paid within the last 24 hours open the Invoiced folder, while invoices paid more than 24 hours ago open the Paid folder.",
        "Pre-Assign Outreach: statuses are now color-coded (Not Reached, No Response, Will Confirm Back, Not Available, Accepted) using the portal theme colors.",
        "Pre-Assign Outreach: the linguists list is now responsive — a table on wide screens, and cards with all details and buttons on narrower screens.",
        "Pre-Assign Outreach: added Distance, Mileage Cost, and Assignments columns (same logic as Pre-Assign), and the list is sorted by distance.",
        "Pre-Assign Outreach: added a 'Contacted By' column showing which team member last contacted each linguist, and the history now shows the member's name and every status change.",
        "Pre-Assign Outreach: linguists without a phone number show a tooltip on the Text button explaining why it is disabled.",
        "Pre-Assign Outreach: the outreach list now automatically includes all active linguists matching the assignment's language (the manual 'Add a linguist' field was removed).",
        "Fixed the OPI Details text to never show an empty ' -  - ' gap: the requestor segment is only included when a requestor exists, so Details now read like 'Client - Requestor - Monthly On-Demand Phone Language Interpretation' (or without the requestor when none is set).",
        "Fixed the audit History to show the Invoicer's name instead of an ID, and to show the previous value as a name (not 'N/A') when a Requestor or Invoicer is changed.",
        "Fixed the Translation Source Language to never create or save an 'N/A' language — assignments without a source language now stay empty instead of showing 'N/A'.",
        "Reminders: manually selected templates are now preserved when the assignment data refreshes.",
        "Timesheets: the Month Total now shows HH:mm (hours and minutes) and the hourly-rate hover popup no longer stays open after the mouse leaves.",
        "Multiple bug fixes and improvements.",
      ],
    },
    {
      version: "0.9.42",
      date: "08-18-2026",
      changes: [
        "Added Persistent Pre-Assign Outreach: Interpretation assignments can now be created without a linguist (while the status is Requested), and the Assignment Profile includes a Pre-Assign Outreach screen to contact linguists one by one.",
        "Outreach records each linguist's status (Not Reached, No Response, Will Confirm Back, Not Available, Accepted) with Text/Email shortcuts, timestamps, and a full history — progress is saved and shared across users and refreshes.",
        "When a linguist accepts, the team can assign them to the assignment directly from the outreach screen.",
      ],
    },
    {
      version: "0.9.41",
      date: "08-14-2026",
      changes: [
        "Added a tooltip to the linguists list in the Pre-Assign Outreach, showing more Linguist Details, with a clickable Address to open Maps or copy it, and properly formatted phone numbers.",
        "Added the ability to watermark multiple PDFs in one operation, with all watermarked files downloaded as a ZIP.",
        "Watermarked files now use the watermark text in the file name (e.g. Document_CONFIDENTIAL.pdf).",
        "Removed the CWS special case from the invoice folder navigation; all invoices now use the normal folder workflow.",
        "Fixed an issue that caused the Invoice One Drive button to open the Paid Folder.",
        "Added multi-timezone display: when the Client and/or Linguist use a timezone different from Eastern Time, the assignment times are now shown in each party's timezone (e.g. 10:00 AM EST / Client: 7:00 AM PDT / Linguist: 4:00 PM CET).",
        "Multi-timezone times now appear in the Assignment Profile, Edit Assignment, and Calendar tooltips.",
        "Added Requestor Groups: organize a client's requestors into departments/business units, each with its own Primary Invoicer and CC recipients.",
      ],
    },
    {
      version: "0.9.40",
      date: "08-12-2026",
      changes: [
        "Corrected an issue that caused wrong calculation for Payment fees.",
        "Improved the History tab to show the change and the difference.",
        "Reminders now use the same rule-based template auto-selection as Send Details.",
        "Fixed an issue that caused the Reminders SMS templates list to be empty, and the wrong template to be pre-selected for assignments.",
      ],
    },
    {
      version: "0.9.39",
      date: "08-07-2026",
      changes: [
        "Improved the Email and Reminder templates with rules to automatically select the most appropriate template based on the assignment's details.",
      ],
    },
    {
      version: "0.9.38",
      date: "08-06-2026",
      changes: [
        "Fixed an issue that caused the client rate to not be saved.",
        "Fixed an issue that caused the wrong Rate to show in the Linguists Payments.",
        "Fixed an issue that caused the discounted invoice to not show the correct final total.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.9.37",
      date: "08-05-2026",
      changes: [
        "Fixed a bug that prevented Simultaneous assignment from showing the correct rate.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.9.36",
      date: "08-04-2026",
      changes: [
        "When editing an assignment that uses the old mileage rate, the Mileage Cost gets updated to the new rate.",
      ],
    },
    {
      version: "0.9.35",
      date: "08-03-2026",
      changes: [
        "Improved the Phone Numbers for Clients & Linguists; Allowing users to add additional numbers to linguists, requestors, and contact persons.",
        "Updated the Mileage Rates in Settings to allow different rates for specific months of a year.",
      ],
    },
    {
      version: "0.9.34",
      date: "06-22-2026",
      changes: [
        "Added a feature to allow users to update Linguist Requirements and keep track of changes.",
        "Improved the Timezone warning when creating Interpretation assignments with clients and/or linguists with different timezones.",
      ],
    },
    {
      version: "0.9.33",
      date: "06-15-2026",
      changes: [
        "Added a tab for 'Contact Persons' in the Client Profile.",
        "Added a feature to allow users to add and manage contact persons for each client.",
      ],
    },
    {
      version: "0.9.32",
      date: "06-11-2026",
      changes: [
        "Added Dialects feature: Allows users to create and manage dialects, as well as assign them to linguists.",
        "Added Country of Origin field to Linguist profile, helpful to know where the linguist is from for country-specific requests.",
      ],
    },
    {
      version: "0.9.31",
      date: "06-11-2026",
      changes: [
        "Added more timezones and improved the timezone selection list.",
      ],
    },
    {
      version: "0.9.30",
      date: "06-05-2026",
      changes: [
        "Added additional measures and logs to better detect the reasons for the server crash.",
        "Fixed an issue that caused the Linguist Address to act incorrrectly.",
      ],
    },
    {
      version: "0.9.29",
      date: "06-04-2026",
      changes: [
        "Added Timezone field to Linguist and Client profiles: Which would help members spot timezone differences between clients and linguists when assigning.",
        "Added a warning when creating assignments with clients and/or linguists with different timezones.",
        "Added the 3 dots menu to Clients: Allowing users to change columns, density, and export the clients list.",
        "Fixed an issue that caused the Clients list filters to not be saved.",
      ],
    },
    {
      version: "0.9.28",
      date: "06-03-2026",
      changes: [
        "Fixed an issue that caused the list of Requestors to not display correctly.",
        "Fixed an issue that caused random server crash.",
        "Fixed an issue with Linguist Merge functionality.",
        "Fixed an issue that caused the Invoice Note to not properly show.",
        "Fixed an issue that caused the Linguist Note to not properly show when selecting a Linguist in the Interpretation Creation.",
      ],
    },
    {
      version: "0.9.27",
      date: "06-02-2026",
      changes: [
        "Added the Discount option to the Invoices.",
        "Fixed an issue that caused Service Notes to not show the date of creation of the note.",
        "Fixed an issue that caused the Timesheet entry to not allow AM/PM changes.",
        "Fixed an issue that caused invoices to a specific client to not be created.",
      ],
    },
    {
      version: "0.9.26",
      date: "05-27-2026",
      changes: [
        "When creating a new assignment, the list of Requestors now shows the number of services per requestor.",
        "Added a quick access button to the Invoice Profile to open the Drive folder that contains the invoice PDF.",
        "Fixed an issue that caused the suggested clock out time to be one hour behind the actual time.",
      ],
    },
    {
      version: "0.9.25",
      date: "05-26-2026",
      changes: [
        "Corrected an issue that caused Details emails to linguists to only have one Contact Person (if the assignment has many).",
        "Corrected an issue that caused dates and names of days to not be in English.",
      ],
    },
    {
      version: "0.9.24",
      date: "05-12-2026",
      changes: [
        "Fixed an issue that caused the Project Number in Translation and Other Services to change unexpectedly.",
        "Fixed an issue that caused Mileage Roundtrip discrepancies.",
      ],
    },
    {
      version: "0.9.23",
      date: "05-11-2026",
      changes: [
        "Added Timesheets to allow the users to track their time using the Portal.",
        "Fixed an issue that caused the mileage to be automatically added to the assignment's cost upon creation.",
        "Added a way to copy the Linguist email from the overlay to send assignment details.",
        "Added 'Watermark PDF' feature under Translations > Documents.",
      ],
    },
    {
      version: "0.9.22",
      date: "05-08-2026",
      changes: [
        "Added the ability to merge duplicate clients.",
        "Added the ability to merge duplicate Linguists.",
        "Fixed an issue that caused Duplicated assignments to have incorrect costs.",
        "Duplicating assignments, now prefills the data into the New Assignment form, allowing users to easily duplicate and edit assignments.",
        "Added the ability to change email, with the necessary safety checks and confirmations.",
        "Added Search history in the Global Search, allowing users to easily access their recent searches.",
        "Global Search now allows searching by language.",
        "The projects in the Linguist and Client Profiles now only show the the months and years where the linguist/client had assignments, instead of showing all months and years.",
      ],
    },
    {
      version: "0.9.21",
      date: "05-07-2026",
      changes: [
        "Address changes to Clients and Linguists are now properly saved into the History",
        "Corrected an issue that caused Service Costs to not show in the Service History.",
        "Added the ability to merge duplicate requestors.",
        "Updated the Client and Linguist Profiles UI to be more user-friendly and intuitive.",
      ],
    },
    {
      version: "0.9.20",
      date: "05-06-2026",
      changes: [
        "Added a Global Search feature: Allowing users to search across all entities in the portal.",
        "Added a 'Quick Create' button to quickly create new services, clients, or linguists from anywhere in the portal.",
        "Updated the Dashboard to show more insights and KPIs, as well as quick links to important sections of the portal.",
        "Address changes to Clients and Linguists are now properly saved into the History",
        "Corrected an issue that caused Service Costs to not show in the Service History.",
        "Added the ability to merge duplicate requestors.",
      ],
    },
    {
      version: "0.9.19",
      date: "05-04-2026",
      changes: ["Fixed some bugs with the invoices."],
    },
    {
      version: "0.9.18",
      date: "04-29-2026",
      changes: [
        "Added Service Types to Settings; Allowing users to add or edit Service Types for different Service Categories.",
        "When creating a new Interpretation, Linguists will now show the number of assignments they had with that Client.",
        "When creating a new Interpretation, Languages will now show the number of assignments that client had.",
        "Added 'Pre-Assign Outreach' feature to Interpretations list: Allowing users to select a client, a language, and an address, and see the list of linguists with distance from the assignment, as well as being able to call, or move forward with the linguist to create the assignment.",
      ],
    },
    {
      version: "0.9.17",
      date: "04-27-2026",
      changes: [
        "Fixed an issue that caused Account Charge to not display correctly.",
        "Updated Address for Linguists and Clients to be split into multiple lines.",
      ],
    },
    {
      version: "0.9.16",
      date: "04-24-2026",
      changes: [
        "Fixed an issue that caused Times in Interpretations list to show as N/A.",
        "Fixed various Calculation issues in Interpretation Creation.",
        "Corrected an issue that caused mileage and other costs to not show correctly in the Interpretation Profile.",
        "Updated the Time Picker to better show AM/PM selection.",
        "Various bug fixes and Quality of Life improvements.",
        "Improved the Reminders to include/exclude requested assignments.",
        "Improved the Documents Encryption process.",
      ],
    },
    {
      version: "0.9.15",
      date: "04-17-2026",
      changes: [
        "Improved the Costs calculation in all services.",
        "Added 'Address', 'Mileage Roundstip', and 'Mileage Cost' to the Interpretations list.",
      ],
    },
    {
      version: "0.9.14",
      date: "04-15-2026",
      changes: [
        "Added 'PDF Protection' feature to the Portal: Allowing users to set upload PDFs and protect them with passwords.",
        "Improved Contact Persons: More than a single contact person can be added to a service.",
        "Updated the Requestors to have a dedicated field for the Phone number Extension, and ensured that the call buttons handle the ext by default.",
        "Fixed an error that caused the Portal to crash when trying to add a new Task.",
        "Bug fixes, issues corrections, and performance improvements.",
      ],
    },
    {
      version: "0.9.13",
      date: "04-14-2026",
      changes: [
        "Improved the Options in the Creation and Editing overlay of all 3 services to be more user-friendly and intuitive.",
        "Corrected an issue that caused OPI assignments to be added to Outlook Calendar.",
        "Bug fixes, issues corrections, and performance improvements.",
      ],
    },
    {
      version: "0.9.12",
      date: "04-08-2026",
      changes: [
        "Improved the Clients and Linguists to have multiple notes.",
        "Added 'Invoice Note' to Client Profile, allowing users to set a default note for each client that will be added to the invoice when the client is selected.",
        "Added Payment Method Notes for invoices; Allowing invoices to have specific notes depending on Paymenth method for each client.",
      ],
    },
    {
      version: "0.9.11",
      date: "04-06-2026",
      changes: [
        "Added 'Service Documents': Allowing users to upload document templates, and quickly fill them when needed.",
        "Added 'Encrypt & Decrypt' feature to quickly hide away any sensitive information from the document, and add it back after translation.",
        "Added 'Reports Insights': A new overlay that provides deterministic insights based on the selected report filters, to help users understand the data and make informed decisions.",
      ],
    },
    {
      version: "0.9.10",
      date: "04-03-2026",
      changes: [
        "Various bug fixes and performance improvements across the whole Portal.",
      ],
    },
    {
      version: "0.9.09",
      date: "04-02-2026",
      changes: [
        //"Added 'Service Documents': Allowing users to upload document templates, and quickly fill them when needed.",
        "Various performance improvements and bug fixes in the Invoices module.",
      ],
    },
    {
      version: "0.9.08",
      date: "04-01-2026",
      changes: [
        "Added Type column to the Interpretations list to easily distinguish between Interpretation Types.",
        "Various performance improvements and bug fixes across the whole Portal.",
      ],
    },
    {
      version: "0.9.07",
      date: "03-31-2026",
      changes: [
        "Added Advanced Analytics reports: Growth & Business Trends, Service Mix & Efficiency, Pricing & Rate Intelligence, Demand, Churn & Forecast, and Market & Growth Opportunities.",
        "Expanded reports interactions with drill-down support and improved chart usability across bars, lines, and pies.",
        "Improved report chart wrappers with better legend placement, spacing, and click behavior to avoid overlap and improve readability.",
        "Added nested pie visualization for revenue mix by category and service type, with clearer color contrast.",
        "Improved report and dashboard tooltip quality, including glossary-enriched explanations for key terms and metrics.",
        "Updated Dashboard General KPI trend percentages to show period-over-period change, and added percentage tooltips with period labels, value change, and percent change context.",
      ],
    },
    {
      version: "0.9.06",
      date: "03-27-2026",
      changes: [
        "Added 'Notifications' feature.",
        "Users can create Notification rules from Settings: Setting up notifications based on specific triggers and conditions related to services and deadlines.",
        "Users can change the Notification sound from Settings.",
        "Added 'Users Tag System': Users can tag other users [using @username] in notes and tasks to notify them and keep them in the loop.",
        "Added 'Service Tag System': Users can tag services [using #projectNumber] in notes and tasks to link them to the service and easily access them.",
      ],
    },
    {
      version: "0.9.05",
      date: "03-19-2026",
      changes: [
        "Fixed an issue that caused Filtering columns in different Data Grids across the Portal to crash.",
        "Adjust Translation and Other Services creation to include Payment Fees, and Additional Linguist Cost (if any).",
        "Generated Documents now show the templates that a merged document consists off.",
        "Corrected an issue that caused incorect values of newly added Linguist Requirements.",
        "Corrected an issue that prevented newly created Linguist Requirements from showing as columns in the Linguists list.",
      ],
    },
    {
      version: "0.9.04",
      date: "03-18-2026",
      changes: [
        "Corrected an issue that caused incorrect Calculations when re-opening assignments marked as Short (< 1 hour).",
        "Interpretation Options are now visible when Completing assignments.",
        "Corrected issues with Reminder Text Message formatting when using the 'Send' button.",
        "Improved Interpretation Details that's generated in the Assignment profile.",
        "Added filtering by Month, and by Expense Status for Expenses.",
        "Added History Tab to Linguist and Client profiles.",
        "When creating invoices, Payment Link is requested to fill for clients with Credit Card as Payment Method.",
        "Client Payment Methods allows better management for clients payments and invoices.",
        "Added 'Payment Methods' and 'Service Types' to Settings > Clients, allowing users to create and edit them.",
        "Added quick links to open Client profile from the Invoice.",
      ],
    },
    {
      version: "0.9.03",
      date: "03-17-2026",
      changes: [
        "Corrected various issues in the Interpretation Creation process.",
        "Changed 'Delete Member' to 'Disable Member', allowing admins to disable accounts without deleting them.",
        "Corrected an issue that caused Expenses to not be created as Finalzied, only as Draft.",
        "Made the list of Expenses showing on hover split between Draft and Finalized.",
        "Added the Grid Options to Linguists Payments: Density, Columns, and Export.",
        "Corrected an issue that caused the Enter key to create an incomplete service.",
      ],
    },
    {
      version: "0.9.02",
      date: "03-16-2026",
      changes: ["Performance improvements."],
    },
    {
      version: "0.9.01",
      date: "03-13-2026",
      changes: [
        "Updated the Grid Options: Columns: Added a button to reset the column changes back to default.",
        "Added a way to Call or Email the requestor from the Interpretation Profile.",
        "Added a feature to prefill Target Language to English if Source Language is not English in Translation Creation.",
        "Corrected an issue in the Linguists Payments that caused the list of services related to a single linguist to not scroll down.",
        "Updated the Settings UI to show the different options under categories for ease of access.",
      ],
    },
    {
      version: "0.9.00",
      date: "03-12-2026",
      changes: [
        "Updated the Linguist Requirements data structure to allow adding new fields as needed.",
        "Added the UI to add or edit Linguist Requirements from the Settings.",
        "Added the 'Document Templates' in the Settings: Allows users to upload a fillable PDF to the Portal.",
        "The Document Template creation screen: Allows linking specific PDF fields to Linguist data.",
        "After the Document Template is created, it can be used to generate a filled PDF for each linguist, with their data automatically filled in the linked fields.",
        "Documents can be generated for multiple selected linguists at once, or for a single linguist from their profile.",
        "Generated Documents can be accessed from the linguists screen for all linguists, or from the linguist profile for a single linguist.",
        "Reports Updates:",
        "- Added the following Business Financial Reports: Expense Trends & Profit & Loss Statement.",
        "- Added the following Client Reports: Client Overview, Client Directory & Contracts, Client Activity & Service Mix, Client Revenue & Profitability, and Client Retention & Risk.",
        "- Added the following Linguist Reports: Linguist Overview.",
        "- Expanded reports UX with dashboard cards, chart visualizations, and side drilldown drawers for detailed record-level analysis.",
        "- Added New Clients and Retained Clients click-to-open drilldowns in Client Revenue & Profitability.",
        "- Implemented and hardened Client Retention & Risk logic by combining retention details, payment ranges, client activity, and outstanding invoices.",
        "- Corrected payment-risk reporting issues (slow payer counts, average payment-day display, duplicate merges, and inclusive risk criteria for overdue or delayed payers).",
        "- Updated payment-ranges backend aggregation so invoice count/revenue and average-day calculations are more accurate and consistent for reporting.",
      ],
    },
    {
      version: "0.8.22",
      date: "02-26-2026",
      changes: [
        "Added the following Invoices & AR Reports: Outstanding Invoices & Invoice Timeline.",
        "Added the following Financial Reports: Profit Margin Analysis.",
        "Corrected an issue that caused the Duplicate Assignment dialog to click more than once, resulting in more than 1 duplicate.",
      ],
    },
    {
      version: "0.8.21",
      date: "02-25-2026",
      changes: [
        "Ensured that all old data [2023 | 2024 | 2025] is perfectly synced between Portal and Tracker.",
        "Added a safety check dialog before updating assignments that are marked as PAID.",
        "Various UI updates.",
        "Added a feature that updates the future assignments of a linguist when their rate is updated.",
        "Added a feature to generate one service per language for Translations and Other Services assignments with multiple target languages.",
        "Added the Density, Columns, and Export options to Linguists.",
        "Added the Density, Columns, and Export options to Invoices.",
        "Fixed an issue that caused services to fail to export.",
        "Updated the UI of the Service History to make it more user-friendly.",
        "Updated the Service History to use the updated Audit Log.",
      ],
    },
    {
      version: "0.8.20",
      date: "02-24-2026",
      changes: [
        "Updated the Expenses Categories and Subcategories to be simpler and more intuitive.",
        "Added quick action buttons in the Linguist and Client profiles to copy, call, text, or email the contact directly from the portal.",
        "Updated Expenses to have a status of Draft or Finalized, and the ability to finalize expenses for a whole month or separately.",
        "Corrected an issue that caused data discrepancies between the Tracker and the Portal for some reports.",
        "Corrected an issue that caused the assignment details in the Calendar to display incorrectly.",
      ],
    },
    {
      version: "0.8.19",
      date: "02-20-2026",
      changes: [
        "Updated the 3 first available reports in Financial Reports: Revenue Summary, Revenue by Client, and Revenue by Service Type.",
      ],
    },
    {
      version: "0.8.18",
      date: "02-18-2026",
      changes: [
        "Updated the Invoice Status to show if the invoice is Paid, Not Paid, or Overdue (Not Paid and more than 30 days past due date).",
        "Updated the Invoice Profile to show additional details on the side.",
      ],
    },
    {
      version: "0.8.17",
      date: "02-17-2026",
      changes: [
        "Added holidays to the Calendar and Interpretation Creation: Helps knowing if the assignment day is a holiday, to avoid scheduling conflicts.",
        "Holidays can be managed from Settings > Holidays: Create, Edit, and Delete.",
        "Added all known holidays in the U.S. up to 2035.",
      ],
    },
    {
      version: "0.8.16",
      date: "02-13-2026",
      changes: [
        "Added Financial Reports 3/7: Revenue Summary, Revenue by Client, and Revenue by Service Type.",
        "When Interpretation assignments are created, and for ease of access, an event will also be created on the Operations Outlook Calendar.",
      ],
    },
    {
      version: "0.8.15",
      date: "02-12-2026",
      changes: [
        "Added Density, Columns, and Export options to the Translations, Other Services lists.",
      ],
    },
    {
      version: "0.8.15",
      date: "02-11-2026",
      changes: [
        "Added Density, Columns, and Export options to the Interpretations list.",
        "Fixed an issue that caused manually set Account Charges to be overridden by the automatic calculation when editing an assignment.",
        "Fixed an issue that caused 2 Hours Minimum to be overridden by the automatic calculation when editing an assignment.",
        "Updated the whole Portal to be responsive and mobile friendly.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.14",
      date: "02-10-2026",
      changes: [
        "Updated the Calendar screen to be responsive and mobile friendly.",
        "Updated the AuditLog to be more efficient, save more space and show more relevant information.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.13",
      date: "02-09-2026",
      changes: [
        "Corrected an issue that caused new invoices to be created with the date of their services, instead of today's date.",
        "Corrected an issue that caused the wrong Project Number to be assigned to Interpretation Projects when Editing.",
        "Separated Total Linguist Pay from Total Cost in the Interpretation assignments.",
        "Added a new field 'Equipment Cost' to Interpretation assignments to account for equipment-related expenses.",
        "Added Unit Type, Unit Count and Unit Rate to Translation assignments and Other Services assignments.",
        "Units will be set from Settings > Unit Types.",
        "Units will allow better tracking of Translation assignments and Other Services assignments, as well as invoicing clients based on different unit types.",
      ],
    },
    {
      version: "0.8.12",
      date: "02-06-2026",
      changes: [
        "Completely removed the old IP link: 135.148.148.37. It now redirects to the new domain: admin.communicationessentialsllc.com.",
      ],
    },
    {
      version: "0.8.11",
      date: "02-04-2026",
      changes: [
        "Fixed multiple issues with the OPI logs and Invoices.",
        "Fixed an issue that caused OPI assignment creation to fail for clients with OPI Minimum & no calls during the month.",
        "Fixed an issue that caused the time in the Calendar Assignment Details to show in 24-hour format instead of 12-hour AM/PM format.",
      ],
    },
    {
      version: "0.8.10",
      date: "02-03-2026",
      changes: [
        "Fixed an issue that caused the Linguist Payments screen to account for deleted assignments.",
      ],
    },
    {
      version: "0.8.09",
      date: "01-30-2026",
      changes: [
        "Added HyperLink feature to the Communication Templates editor, Reminders, and Details overlays.",
        "Send Details for interpretations will always be visible now (Regardless of Assignment Status or Date).",
        "All assignments can be separately marked as paid, regardless of the client type. (Won't be invoiceable later).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.08",
      date: "01-29-2026",
      changes: [
        "Invoices now can have a payment link, and will show a QR code as well as a clickable link in the PDF export.",
        "Invoices will now have the ability to split Payment Fees from the service.",
        "Reminders and Details will now have only the Linguist's name, and will automatically adjust to the assignment type.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.07",
      date: "01-28-2026",
      changes: [
        "Updated the UI: Filters will now be showing on the left, and the buttons have labels.",
        "Added 'Reminders' to the Interpretations list screen: Allowing users to get assignments reminders to send to Linguists.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.06",
      date: "01-27-2026",
      changes: [
        "Updated the Email Templates to 'Communication Templates' to have SMS templates included.",
        "Corrected an issue that caused Interpretation Projects to be created with the wrong Project Number.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.05",
      date: "01-26-2026",
      changes: [
        "Added the infrastructure of the Reports section.",
        "Added Amer's and the Operations Digital Cards.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.04",
      date: "01-23-2026",
      changes: [
        "Corrected an issue that caused Linguist Pay to be wrong when the assignment is Simultaneous.",
        "Updated the Linguists' Payments screen to show the remaining balance to be paid.",
        "Linguists' Payments now save which services have been paid for each linguist.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.03",
      date: "01-22-2026",
      changes: [
        "Added all the Expenses data from the Tracker, dating back to 2023.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.02",
      date: "01-21-2026",
      changes: [
        "Adjusted Linguists Rates to support decimal values.",
        "Corrected an issue that prevented zeroing the charges when the assignment is cancelled.",
        "Updated the invoices to set ServicesDate based on related services and adjusted missing ServicesDate to one month before the invoice date.",
        "Added the ability to access the Interpretation assignment profile from the Calendar.",
        "Added the 'Copy Tracker Row' button in the Profile of all Services, allowing users to quick paste the assignment/project details from Portal to Tracker.",
        "Added the ability to create Categories and SubCategories for expenses in the Settings.",
        "Added Expenses Management for Admins: An expense can be created, edited, and tracked.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.01",
      date: "01-16-2026",
      changes: [
        "Added a feature to refresh data of an assignment or profile when another user updates it.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.0",
      date: "01-15-2026",
      changes: [
        "Added a feature to lock assignments when being edited by another Member.",
        "Added a feature to allow seeing who is also viewing other assignments, to avoid conflicts.",
        "Added Member Profile Image.",
        "Corrected an issue that caused the Service Creation overlay to open with no dropdowns data.",
        "Added Password Strength calculation when changing or resetting the Password.",
        "Improved the responsiveness of the Overlays.",
        "Improved the User Interface across the whole Portal.",
        "Corrected an issue that caused assignments to not be saved when Account Charge was zeroed out.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.16.1",
      date: "01-13-2026",
      changes: [
        "Improved the History keeping feature of the whole Portal.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.15",
      date: "01-12-2026",
      changes: [
        "Added a check to the Project Number to prevent duplications.",
        "Added a filter to the Calendar, allowing to show any combination os service statuses.",
        "Added the option to Split the languages in the Invoice.",
        "Corrected an issue that caused service Duplication to fail.",
        "Improved the Automatic Calculation of charges in New/Edit Service screen.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.14",
      date: "01-09-2026",
      changes: [
        "Improved the Invoice address and export process.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.13",
      date: "01-08-2026",
      changes: [
        "Added the ability to export Invoices with or without OPI details.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.12",
      date: "01-07-2026",
      changes: [
        "Corrected an issue that caused incorrect Project Numbers to be generated when editing an Interpretation.",
        "Corrected an issue that caused duplicated assignments to have incorrect Project Numbers.",
        "Allowed the changing of the Client Name and Address directly from the Invoice edit screen.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.11",
      date: "01-06-2026",
      changes: [
        "Corrected an issue that caused Times to be displayed incorrectly due to Timezone changes.",
        "Invoices can be marked as Paid, either from the Invoice Profile or from the Invoices list (multi-select supported).",
        "Private Persons assignments can be marked as Paid.",
        "Corrected an issue that caused N/A linguists to be recreated when setting Linguist to N/A in Interpretation Creation/Edit.",
        "Added a way to see the number of tasks for the user from the side menu.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.10",
      date: "01-05-2026",
      changes: [
        "Invoices are fully functional: Create, View, Edit, Delete, and Export as PDF.",
        "Invoice services' mileage can be split from the service itself.",
        "Interpretation Creation: Added the ability to use Mileage Rates from the settings, depending on the year of the assignment.",
        "Interpretation Creation: Now handles correctly linguists with updated rates.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.09",
      date: "01-02-2026",
      changes: [
        "Invoices Feature: Added the ability to create, view, and manage invoices directly within CE Portal.",
        "Invoices can be exported as PDF for easy sharing and record-keeping.",
        "Invoice Details can be",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.08",
      date: "12-30-2025",
      changes: [
        "Implemented Email Templates: Created customizable email templates for various scenarios such as assignment creation, completion, and reminders. Users can now select and edit email templates to suit their communication needs.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.07",
      date: "12-26-2025",
      changes: [
        "Corrected an issue that caused new Translations to have Bengali as source language.",
        "Improved the Filters of all DataGrids to prevent issues when filtering amounts.",
        "Linguists Payments Total Amount filter now accepts numeric comparisons (>, <, >=, <=).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.06",
      date: "12-25-2025",
      changes: [
        "Updated the Linguists' Payments to have more details.",
        "Added Auto-Generated Tasks feature, which automatically creates tasks based on specific conditions related to services and invoicing.",
        "Added the ability to adjust Interpretation assignments to allow durations less than 1 hour (for scheduling or calling assignments).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.05",
      date: "12-24-2025",
      changes: [
        "Added a redirect to 404 page when a user without Admin role tries to access Admin-only routes.",
        "Added a 404 Not Found page to inform users when they try to access a non-existent page.",
        "Added color coding to various Service Columns for better visual distinction.",
      ],
    },
    {
      version: "0.7.04",
      date: "12-23-2025",
      changes: [
        "Added Calendar screen to view assignments in a calendar format.",
        "Added Members management screen, allowing Admins to manage CE Portal members.",
        "Updated the Settings screen: Members can now change their Name, and update their passwords.",
        "Added the 'Forgot Password' feature to the Login screen, allowing users to reset their password via email.",
        "Various bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.03",
      date: "12-18-2025",
      changes: [
        "Corrected an issue that prevented Editing or Completing assignments from the Dashboards.",
        "Corrected an issue that prevented all the Clients from showing when adding a new Interpretation assignment.",
        "Updated the UI's font to use the latest branding guidelines.",
      ],
    },
    {
      version: "0.7.02",
      date: "12-17-2025",
      changes: [
        "Corrected an issue that caused a black screen when trying to edit a Requestor",
        "Added the Context Menu to all the DataGrids in all the Dashboards.",
        "Corrected an issue that prevented Two Hour Minimum assignments to be created correctly.",
      ],
    },
    {
      version: "0.7.01",
      date: "12-16-2025",
      changes: [
        "Added Tasks to each service profile (Interpretations, Translations, Other Services), allowing users to create, assign, and manage tasks related to each service directly from its profile.",
        "Improved the Date Filter component to have month/day/year selection for more precise filtering.",
        "Corrected an issue that prevented the Clients that don't have any requestors from being displayed correctly.",
      ],
    },
    {
      version: "0.7.00",
      date: "12-15-2025",
      changes: [
        "Updated the UI colors to match the latest branding guidelines.",
        "Completely updated the UI of the Portal to make it more user-friendly and visually appealing.",
      ],
    },
    {
      version: "0.6.94",
      date: "12-12-2025",
      changes: [
        "Corrected an issue that prevented the Project Number from being set up in new Translation and Other Services assignments.",
        "Updated the Logo in the Login screen, Sidebar, and favicon to the latest CE Logo.",
        "Added a confirmation dialog when duplicating assignments to prevent accidental duplications.",
        "Improved the Context Menus across all services to enhance usability and accessibility.",
        "Corrected an issue that caused Translation assignments to have a wrong Linguist.",
        "Corrected an issue that prevented new Translation and Other Services to be created.",
        "Corrected an issue that prevented the Tasks to be deleted.",
        "Improved the Tasks Filtering system",
        "Added all the Linguists columns from the Tracker to the Portal.",
        "Corrected an issue that prevented the Linguist's Mode to be saved.",
      ],
    },
    {
      version: "0.6.93",
      date: "12-11-2025",
      changes: [
        "Filters across all DataGrids are now saved automatically, and will persist until manually cleared.",
        "Added Tasks feature to CE Portal, allowing users to create, assign, and manage tasks within the portal.\nTasks can be associated with specific entities such as Clients, Linguists, or Services.\nUsers can set due dates, priorities, and statuses for each task.\nAdded notifications for task assignments and upcoming due dates.\nImproved overall UI/UX for better navigation and usability.",
        "Added a Context Menu to the Services DataGrids in CE Portal, accessible via right-clicking on a row.\nThe Context Menu provides quick access to common actions such as Edit, Add Task or Duplicate, and more to come, depending on the entity type.",
        "Added Version Check feature to CE Portal, which periodically checks for new versions of the application.\nWhen a new version is detected, a notification is displayed to the user with an option to reload the page and update to the latest version.",
      ],
    },
    {
      version: "0.6.92",
      date: "12-10-2025",
      changes: [
        "Corrected the collapsed sidebar tooltip display issue: Tooltip now shows correctly when the sidebar is collapsed.",
        "Corrected an issue that prevented opening specific service profiles or client/linguist profiles by id from the link.",
        "Improved the Interpretation creation/update:",
        "- The Enter button works correctly to submit the form.",
        "- Corrected an issue that caused empty fields to have 0.00 values.",
        "- Added a checkbox for 2 Hours Minimum next to the Rate field, as some assignments are billed with a 2-hour minimum regardless of the linguists minimum charge.",
        "Corrected an issue that caused the Client Interpretation Rates by language to only show 3 exceptions, instead of all the exceptions added for that client.",
        "Improved the Client Interpretation Rates creation UI: The Exceptions are now scrollable down.",
        "Added the ability to clear each filter on its own in all the DataGrids.",
        "The filter icon for each column now indicates when a filter is active.",
      ],
    },
    {
      version: "0.6.91",
      date: "12-09-2025",
      changes: [
        "Improved Dashboard to prevent a white screen issue on load.",
        "Added a confirmation dialog for logout to prevent accidental sign-outs.",
        "Fixed the tooltip display issue on the sidebar icons in collapsed mode.",
        "Corrected the search behavior in the Date Filters across the Portal.",
      ],
    },
    {
      version: "0.6.90",
      date: "12-05-2025",
      changes: [
        "Improved the filtering and sorting performance in all DataGrids in CE Portal.",
        "Added links to the Client and Linguist profiles from the Interpretation, Translation, and Other Services profiles.",
        "Imported all the Invoices from the Tracker, dating back to 2023.",
        "Created the Invoices screen to manage all invoices in CE Portal.",
        "Added the ability to add invoices from the Interpretation, Translation, and Other Services services.",
        "Added a link to the Invoice profile from the Interpretation, Translation, and Other Services profiles.",
      ],
    },
    {
      version: "0.6.8",
      date: "11-28-2025",
      changes: [
        "Added the new filtering system to all DataGrids in CE Portal, mimicking the same filtering system in Excel.",
      ],
    },
    {
      version: "0.6.7",
      date: "11-27-2025",
      changes: [
        "Updated CE Portal's data to have the latest assignments from the Tracker as of 11-27-2025.",
        "Imported the invoices from the Tracker, dating back to 2023.",
        "Created the Invoices screen to manage all invoices in CE Portal.",
        "Improved the filtering options in the Invoices screen, mimicking the same filtering system in Excel.",
        "Applied the new Excel-like filtering system to all services lists (Translations, Interpretations, Other Services).",
        "Applied the new Excel-like filtering system to the Clients and Linguists lists.",
      ],
    },
    {
      version: "0.6.6",
      date: "10-06-2025",
      changes: [
        "Added OPI Minimum Charge to Client Creation/Edit screen [Can only be set if OPI is checked under Services].",
        "Added: Interpretation Rates for Clients: We can create different rates by language for each client.",
        "Added: OPI Logs upload to the Interpretations: Upon uploading the CSV logs from IM, CE Portal will process it and will add all the entries to the database, as well as allow the user to download the XLSX final sheet, and the PDF detailed logs.",
      ],
    },
    {
      version: "0.6.5",
      date: "08-19-2025",
      changes: [
        "History is now fully shown on all the services.",
        "Added totals for the currently selected months in the Translations, Interpretations, and Other Services lists.",
      ],
    },
    {
      version: "0.6.4",
      date: "08-15-2025",
      changes: [
        "Added Client Rate Discount, which helps reduce the client's rate and do all the calculations, in case of a cancellation or if rate reduction is needed.",
        "Filtering by Year and Months will now be saved [In All Services], and the filter will persist until changed/removed.",
        "Added a Note field in the Complete Interpretation assignment screen.",
      ],
    },
    {
      version: "0.6.3",
      date: "08-14-2025",
      changes: [
        "Added the ability to make an Interpretation assignment recurring (Daily, Weekly, or Monthly).",
      ],
    },
    {
      version: "0.6.2",
      date: "08-13-2025",
      changes: [
        "Corrected an issue where the 'Open Drive Folder' opened only in 'Translations 2025', now it opens even old projects from 2023 & 2024.",
        "Added the ability to delete Translation, Interpretation and Other Services assignments [In case one was created by mistake].",
      ],
    },
    {
      version: "0.6.1",
      date: "05-16-2025",
      changes: [
        "Interpretation Completion: Now the Actual Start and Actual End will be automatically filled by the Start and End times.",
        "All Services: Sorting is now being saved, your sorts will persist until you remove them.",
        ".",
        ".",
      ],
    },
    {
      version: "0.6.0",
      date: "04-29-2025",
      changes: [
        "Add Interpretation: Corrected an issue that caused Linguist Pay to be less than 1 hour.",
        "Linguist Pay & Account Charge now correctly round up to the next 15 minutes segment.",
        "Corrected an issue that caused issues when sorting by Time Start/End in Interpretations List.",
        "In Interpretation Profile, the 'Check Mileage' button now doesn't show if it's a virtual assignment.",
        "In Interpretation Profile, the 'Call' button for the Contact Person only shows if a phone number has been added.",
        "All Services: Filtering by a column now is saved, and your filters will now persist until you remove them manually (like in the Tracker).",
      ],
    },
    {
      version: "0.5.9",
      date: "04-23-2025",
      changes: [
        "Updated the UI.",
        "Added the Help page, which contains videos showing how to navigate through CE Portal.",
        "Moved the Changelog to the Help page.",
      ],
    },
    {
      version: "0.5.8",
      date: "04-21-2025",
      changes: [
        'Corrected Total Duration "Add Interpretation Assignment": It will now count the biggest between Actual Duration and Duration.',
        'Added the option to edit the Total Duration manually in "Add Interpretation Assignment".',
        "Corrected an issue that prevented the correct saving of Assignment Checking Date (All Services).",
        "Added time to the Note's Creation Date.",
        "Corrected an issue that prevented Completing Other Service assignments.",
        "Corrected an issue that prevented Delivery Date to be set correctly.",
        "Corrected an issue that prevented the saving of the Delivery Type, and Payment Type when Completing an assignment.",
        'Corrected the "New Client" form.',
        "Corrected an issue that caused the Linguist's Mode to not be saved.",
        "Corrected an issue that caused the Bottom Message to always show in the day theme.",
      ],
    },
    {
      version: "0.5.7",
      date: "04-16-2025",
      changes: [
        "Updated the Month & Year Picker in the Dashboard allowing multi month selection in the ALL Dashboard.",
      ],
    },
    {
      version: "0.5.6",
      date: "04-14-2025",
      changes: ["Updated the Month & Year Picker in All Services lists."],
    },
    {
      version: "0.5.5",
      date: "04-10-2025",
      changes: [
        "Corrected some dashboard issues with charts not showing for future dates.",
        "Corrected a server OS difference issue that caused Clients Dashboard to show no data.",
      ],
    },
    {
      version: "0.5.4",
      date: "04-09-2025",
      changes: [
        "Corrected an issue that prevented pages from showing after refreshing.",
      ],
    },
    {
      version: "0.5.3",
      date: "04-08-2025",
      changes: [
        "Added 'Change Category' to Services, allowing changing a category of a service after it's created.",
      ],
    },
    {
      version: "0.5.2",
      date: "04-07-2025",
      changes: [
        "Applied server-side corrections.",
        "Added Contact Person (Name and Number) To Interpretations, with the Quick Action to call them from CE Portal directly.",
        "Admin: Corrected Dashboard > Clients > Type: Clicking on N/A now correctly filters the list of clients.",
        "Admin: In Dashboard > General: Instead of 'Last Month', the Card will now show the name of the last month ('Mar' if this month if 'Apr').",
        "Admin: In Dashboard > General: Added a Trend icon, and ensured its color and the percentage color represents the trend.",
        "Replaced the Years filter in Interpretations, now it is possible to filter by both Month and Year.",
        "Replaced the Years filter in Translations, now it is possible to filter by both Month and Year.",
        "Replaced the Years filter in Other Services, now it is possible to filter by both Month and Year.",
        "Added Login Logs: Which allows knowing who logged in, and when, and help protecting access and knowing when someone from outside of CE tries to login.",
      ],
    },
    {
      version: "0.5.1",
      date: "04-04-2025",
      changes: [
        "Added: Changelog which allows tracking what's added and what's changed in the portal.",
        "Added: Placeholder screen for Management pages.",
        "Added: Bruteforce protection, limiting login requests to 5 requests per minute.",
        "Corrected Interpretation Quick Actions to only show if the phone number, email, or address are available.",
        "Corrected Translation Quick Actions to only show if the email is available.",
        "Corrected Other Services Quick Actions to only show if the email is available.",
        "Corrected the Add new Note button possition (Was under the Assignment Actions).",
        "Imported the latest assignments from the Tracker: CE Portal is up to date to Tracker as of 04-04-25 at 01PM EST.",
      ],
    },

    {
      version: "0.5.0",
      date: "04-03-2025",
      changes: ["Initial release of MVP as shown to Amer."],
    },
  ];

  // Function to open video link in a new tab
  const handleVideoClick = (link) => {
    window.open(link, "_blank");
  };

  return (
    <Box
      p={isMobile ? "0.5rem 0.5rem 0 0.5rem" : "1rem 1rem 0 1rem"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        //borderRadius="12px"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          overflowX: "visible",
          minHeight: 0,
          mb: 1,
          mx: isMobile ? 0 : 1,
          mt: isMobile ? 1 : 2,
          //boxShadow: theme.shadows[3],
        }}
      >
        {/* Tabs — Changelog | Videos (standard tab bar, same on every screen size) */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            minWidth: 0,
            width: "100%",
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabsChange}
            aria-label="Help tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              bgcolor: theme.palette.background.alt,
              "& .MuiTab-root": {
                borderRadius: "12px",
              },
            }}
          >
            <CustomTab label="Changelog" {...a11yProps(0)} />
            <CustomTab label="Videos" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Changelog panel */}
        <CustomTabPanel
          value={tabValue}
          index={0}
          sx={{
            flex: 1,
            width: "100%",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
        {/* Changelog Section (heading removed 2026-10-05, Amine — the tab label already says "Changelog") */}
        <Box mt={isMobile ? 3 : 0}>
          {versions.map((version, index) => (
            <Box
              key={"" + index}
              sx={{
                mb: 2,
                p: isMobile ? 0 : 1,
              }}
            >
              <CardDashboardItemVerticalMulti
                title={"Version: " + version.version}
                subTitle={"Released on: " + version.date}
                content={version.changes}
              />
            </Box>
          ))}
        </Box>
          </Box>
        </CustomTabPanel>

        {/* Videos panel */}
        <CustomTabPanel
          value={tabValue}
          index={1}
          sx={{
            flex: 1,
            width: "100%",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
        {/* Tutorial Videos Section */}
        <Box mb={4}>
          <Typography
            variant="h4"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ mb: 3 }}
          >
            Here are some videos to show you how certain features of CE Portal
            work
          </Typography>

          {tutorialVideos.map((category, categoryIndex) => (
            <Box key={categoryIndex} mb={4} sx={{ px: isMobile ? 0 : 1 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={theme.palette.primary.main}
                sx={{ mb: 2, pl: isMobile ? 0 : 1 }}
              >
                {category.category}
              </Typography>

              <Grid container spacing={2}>
                {category.videos.map((video, videoIndex) => (
                  <Grid key={videoIndex} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card
                      sx={{
                        height: "100%",
                        backgroundColor: theme.palette.background.default,
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.15)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleVideoClick(video.link)}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            component="div"
                            fontWeight="bold"
                            color={theme.palette.primary.main}
                            gutterBottom
                          >
                            {video.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={theme.palette.primary.text2}
                          >
                            {video.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
          </Box>
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default Help;
